import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchSendLoginCode } from '@/service/api/account';
import { getClientId } from '@/utils/clientId';

export interface UseCaptchaOptions {
  /** 倒计时时长（秒），默认 60 秒 */
  seconds?: number;
}

export function useCaptcha(options: UseCaptchaOptions = {}) {
  const { seconds = 60 } = options;

  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [isCounting, setIsCounting] = useState(false);
  const [phone, setPhone] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = useCallback((initialCount = seconds) => {
    setCount(initialCount);
    setIsCounting(true);
  }, [seconds]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCount(0);
    setIsCounting(false);
  }, []);

  useEffect(() => {
    if (isCounting) {
      timerRef.current = setInterval(() => {
        setCount((prev) => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            setIsCounting(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isCounting]);

  const isPhoneValid = (phoneNumber: string) => {
    const trimmed = phoneNumber.trim();
    if (!trimmed) {
      return { valid: false, message: '请输入手机号' };
    }
    if (!/^1[3-9]\d{9}$/.test(trimmed)) {
      return { valid: false, message: '请输入有效的 11 位手机号' };
    }
    return { valid: true, message: '' };
  };

  /**
   * 发送登录手机号验证码
   */
  const getCaptcha = async (phoneNumber: string): Promise<boolean> => {
    const { valid, message } = isPhoneValid(phoneNumber);
    if (!valid) {
      throw new Error(message);
    }

    if (loading || isCounting) {
      return false;
    }

    setPhone(phoneNumber.trim());
    setLoading(true);

    try {
      const res = await fetchSendLoginCode({
        phone: phoneNumber.trim(),
        client_id: getClientId(),
      });
      if (res?.sent) {
        start(seconds);
        return true;
      }
      throw new Error('获取验证码失败，请稍后重试');
    } catch (error) {
      const msg = error instanceof Error ? error.message : '获取验证码失败，请稍后重试';
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  const label = isCounting
    ? `${count}s`
    : count === 0 && !isCounting
    ? '获取验证码'
    : '重新发送';

  return {
    label,
    count,
    isCounting,
    loading,
    phone,
    getCaptcha,
    start,
    stop,
    isPhoneValid,
  };
}
